import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { AgreementDetailedDto, AgreementsClient, AgreementUpdateDto, TransitionRequestDto, UnityWorkflowState } from '@unity/clients';

import { BannerAlertSharedComponent } from '@unity/components/shared/presentation/banner-alert-shared/banner-alert-shared.component';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { MultiselectCommunityControlComponent } from '@unity/forms/multiselect-community-control/multiselect-community-control.component';
import { SelectorDateControlComponent } from '@unity/forms/selector-date-control/selector-date-control.component';
import { SelectorDateRangeControlComponent } from '@unity/forms/selector-date-range-control/selector-date-range-control.component';
import { TextareaControlComponent } from '@unity/forms/textarea-control/textarea-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { TextboxCurrencyControlComponent } from '@unity/forms/textbox-currency-control/textbox-currency-control.component';
import { TextboxReadonlyControlComponent } from '@unity/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { dateFormControl, optionalNumericFormControl, optionalStringFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { forkJoin } from 'rxjs';

class AgreementUpdateSchema implements FormGroupControlSchema<AgreementUpdateDto> {
  public name: FormControl<string>;
  public description?: FormControl<string | null>;
  public award_amount: FormControl<number | null>;
  public client_standard_allocation_amount: FormControl<number | null>;
  public client_share_of_award_amount: FormControl<number | null>;
  public community_ids: FormControl<number[]>;
  public agreement_date: FormControl<Date>;
  public start_date: FormControl<Date>;
  public end_date: FormControl<Date>;

  public constructor(agreement?: AgreementDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: agreement?.name });
    this.description = optionalStringFormControl({ disabled: true, value: agreement?.description });
    this.award_amount = optionalNumericFormControl({
      disabled: true,
      value: agreement?.award_amount,
      validators: [Validators.min(0.01), Validators.max(99999999.99)],
    });
    this.client_standard_allocation_amount = optionalNumericFormControl({
      disabled: true,
      value: agreement?.client_standard_allocation_amount,
      validators: [Validators.min(0.01), Validators.max(99999999.99)],
    });
    this.client_share_of_award_amount = optionalNumericFormControl({
      disabled: true,
      value: agreement?.client_share_of_award_amount,
      validators: [Validators.min(0.01), Validators.max(99999999.99)],
    });
    this.community_ids = new FormControl<number[]>(
      { disabled: true, value: agreement?.communities?.map(c => c.id) ?? [] },
      { nonNullable: true }
    );
    this.agreement_date = dateFormControl({ disabled: true, value: agreement ? new Date(agreement?.agreement_date) : undefined });
    this.start_date = dateFormControl({ disabled: true, value: agreement ? new Date(agreement?.start_date) : undefined });
    this.end_date = dateFormControl({ disabled: true, value: agreement ? new Date(agreement?.end_date) : undefined });
  }
}

@Component({
  selector: 'unity-manage-agreement-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_GRIDLAYOUT,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxControlComponent,
    TextboxCurrencyControlComponent,
    SelectorDateControlComponent,
    MultiselectCommunityControlComponent,
    SelectorDateRangeControlComponent,
    TextboxReadonlyControlComponent,
    AuthorizeDirective,
    FormLayoutComponent,
    BannerAlertSharedComponent,
    PanelManageSharedComponent,
    ActionDeleteSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
    TextareaControlComponent,
  ],
  templateUrl: './manage-agreement-page.component.html',
  styleUrl: './manage-agreement-page.component.scss',
})
export class ManageAgreementPageComponent extends AbstractManageComponent<AgreementDetailedDto, AgreementUpdateDto, AgreementUpdateSchema> {
  private readonly agreementsClient = inject(AgreementsClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.agreementsClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<AgreementUpdateSchema>) =>
    forkJoin({
      updateAgreement: this.agreementsClient.updateOne(resourceId, form.getRawValue()),
      updateAgreementCommunities: this.agreementsClient.updateCommunitiesInOne(resourceId, form.getRawValue().community_ids),
    });

  protected override deleteResourceFn$ = (resourceId: number) => this.agreementsClient.deleteOne(resourceId);

  protected override availableTransitionsFn$ = (resourceId: number) => this.availableTransitionsClient.getAllForAgreementAsMini(resourceId);

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForAgreement(resourceId, transition);

  protected override newControlsFn(dto?: AgreementDetailedDto) {
    return new AgreementUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'Agreement:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Agreement:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Agreement:Delete';
  protected override resourceName = 'Agreement';
  protected override $resourceId = this.$agreementId;
  protected override readonly $contentHeader = computed(() => `Agreement: ${this.$resource()?.name}`);

  protected readonly $contentHeaderSecondary = computed(() => `Program: ${this.$resource()?.program.name}`);

  protected readonly $isDraft = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Draft);
  protected readonly $isDisabled = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Disabled);
  protected readonly $hasClientExpenses = computed(() => this.$resource()?.supports_client_expenses === true);

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
